import cloneDeep from 'lodash/cloneDeep';
import type {
  ChangeTrackingEntityIdentity,
  FormChangeSummary,
  FormChangeTrackerOptions,
} from '@typeDefinitions/changeTrackingTypes';

import { rebaseHydratedFields } from './findChangedFields';

const identitiesMatch = (
  left: ChangeTrackingEntityIdentity | undefined,
  right: ChangeTrackingEntityIdentity | undefined,
): boolean => left !== undefined && String(left) === String(right);

export const createFormChangeTracker = <T, TForm extends string>(
  options: FormChangeTrackerOptions<T, TForm>,
) => {
  let baseline: T | null = null;

  const setBaseline = (value: T) => {
    baseline =
      options.getIdentity(value) === undefined ? null : cloneDeep(value);
  };

  const clearBaseline = () => {
    baseline = null;
  };

  const prepare = (value: T): FormChangeSummary<TForm> => {
    if (
      baseline === null ||
      !identitiesMatch(
        options.getIdentity(baseline),
        options.getIdentity(value),
      )
    ) {
      return { changedForms: [] };
    }

    return {
      changedForms: options.findChangedForms(baseline, value),
    };
  };

  const synchronizeHydration = (before: T, after: T) => {
    if (
      baseline === null ||
      !identitiesMatch(
        options.getIdentity(baseline),
        options.getIdentity(before),
      ) ||
      !identitiesMatch(options.getIdentity(before), options.getIdentity(after))
    ) {
      return;
    }

    baseline = rebaseHydratedFields(baseline, before, after);
  };

  return {
    clearBaseline,
    prepare,
    setBaseline,
    synchronizeHydration,
  };
};
